import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {HTMLElement | null} */
  #productCard = null;
  /** @type {string} */
  #defaultVariantId = '';

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#productCard = this.closest('product-card');
    this.#defaultVariantId = this.querySelector('input[name="id"]')?.value || '';

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.addEventListener('click', this.#handleSizeSelection);
    this.addEventListener('click', this.#toggleInlineQuickAddOnMobile, { capture: true });
    this.#productCard?.addEventListener('pointerenter', this.#handleProductCardHoverEnter);
    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.removeEventListener('click', this.#handleSizeSelection);
    this.removeEventListener('click', this.#toggleInlineQuickAddOnMobile, { capture: true });
    this.#productCard?.removeEventListener('pointerenter', this.#handleProductCardHoverEnter);
    document.removeEventListener('click', this.#handleDocumentClick);
    this.#productCard = null;
  }

  /**
   * Toggles inline quick-add controls for mobile product cards.
   * First tap opens inline sizes or CTA, subsequent tap performs action.
   * @param {MouseEvent} event
   */
  #toggleInlineQuickAddOnMobile = (event) => {
    if (!isMobileBreakpoint()) return;
    if (this.dataset.disallowBuy === 'true') return;
    if (!(event.target instanceof Element)) return;

    const quickAddButton = event.target.closest('.quick-add__button');
    if (!(quickAddButton instanceof HTMLButtonElement)) return;
    if (!this.contains(quickAddButton)) return;

    const hasInlineSizeSelector = this.dataset.hasSizeSelector === 'true';
    const hasInlineAddButton = this.dataset.quickAddButton === 'add';
    if (!hasInlineSizeSelector && !hasInlineAddButton) return;
    const allowInlineSizeSelectorOnMobile = this.dataset.mobileInlineCardSwatches !== 'false';

    const isChooseButton = quickAddButton.classList.contains('quick-add__button--choose');
    const isAddButton = quickAddButton.classList.contains('quick-add__button--add');

    if (hasInlineSizeSelector && isChooseButton && !allowInlineSizeSelectorOnMobile) {
      return;
    }

    if (!this.hasAttribute('stay-visible')) {
      event.preventDefault();
      event.stopPropagation();
      this.setAttribute('stay-visible', '');
      return;
    }

    if (hasInlineSizeSelector && isChooseButton) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (hasInlineAddButton && isAddButton) return;
  };

  /**
   * Closes inline quick-add when tapping outside.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!isMobileBreakpoint()) return;
    if (!this.hasAttribute('stay-visible')) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.removeAttribute('stay-visible');
  };

  /**
   * Reset quick-add state to show the variant selector each time card is hovered again.
   * This only applies to single-option size-selector cards.
   */
  #handleProductCardHoverEnter = () => {
    if (this.dataset.disallowBuy === 'true') return;
    if (this.dataset.hasSizeSelector !== 'true') return;
    if (this.hasAttribute('stay-visible')) return;
    if (isMobileBreakpoint()) return;

    this.setAttribute('data-quick-add-button', 'choose');

    const sizePills = this.querySelectorAll('.quick-add__size-pill');
    sizePills.forEach((pill) => {
      pill.setAttribute('aria-pressed', 'false');
    });

    const variantInput = this.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;

    if (this.#defaultVariantId) {
      variantInput.value = this.#defaultVariantId;
      const defaultPill = this.querySelector(`.quick-add__size-pill[data-variant-id="${this.#defaultVariantId}"]`);
      if (defaultPill instanceof HTMLButtonElement) {
        variantInput.disabled = defaultPill.dataset.variantAvailable !== 'true';
      } else {
        variantInput.disabled = false;
      }
    }
  };

  /**
   * Handles size pill selection for Mileoff product cards.
   * On selection, switches quick-add state from "choose" to "add" and sets the variant id.
   * @param {MouseEvent} event
   */
  #handleSizeSelection = (event) => {
    if (this.dataset.disallowBuy === 'true') return;
    if (!(event.target instanceof Element)) return;

    const sizeButton = event.target.closest('.quick-add__size-pill');
    if (!(sizeButton instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = sizeButton.dataset.variantId;
    const variantAvailable = sizeButton.dataset.variantAvailable === 'true';
    if (!variantId) return;

    const sizePills = this.querySelectorAll('.quick-add__size-pill');
    sizePills.forEach((pill) => {
      pill.toggleAttribute('aria-pressed', pill === sizeButton);
    });

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
      variantInput.disabled = !variantAvailable;
    }

    if (variantAvailable) {
      this.setAttribute('data-quick-add-button', 'add');
      this.setAttribute('stay-visible', '');
    }

    // Keep product card link in sync with chosen variant.
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const link = productCard?.getProductCardLink();
    if (link?.href) {
      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    }

    const addToCartContent = this.querySelector('.quick-add__button--add .add-to-cart-text__content > span > span');
    if (addToCartContent instanceof HTMLElement) {
      const addLabelFromData = this.dataset.addLabel || 'Add to cart';
      const addLabel = addLabelFromData.trim().toLowerCase() === 'add' ? 'Add to cart' : addLabelFromData;
      addToCartContent.textContent = addLabel;
    }
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.removeAttribute('stay-visible');
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    if (this.dataset.disallowBuy === 'true') return;

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    const shouldKeepCardExpanded =
      !isMobileBreakpoint() || this.dataset.hasSizeSelector !== 'true' || this.dataset.mobileInlineCardSwatches !== 'false';

    if (!shouldKeepCardExpanded) return;

    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.activeQuickAdd = this;
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid, options = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitleLink = /** @type {HTMLAnchorElement | null} */ (productGrid.querySelector('.view-product-title a'));
      const productTitle = document.createElement('a');
      productTitle.textContent = productTitleLink?.textContent?.trim() || this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = productTitleLink?.href || options.productUrl || this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#applyQuickBuyOptionLabelSetting(modalContent);
    this.#injectQuickBuyColourSwatches(modalContent, options.selectedProductId);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Applies the quick buy option label visibility to modal variant pickers.
   * @param {Element} modalContent
   */
  #applyQuickBuyOptionLabelSetting(modalContent) {
    const hideLabels = this.dataset.showOptionLabels === 'false' ? 'true' : 'false';
    const variantPickers = modalContent.querySelectorAll('variant-picker');

    for (const variantPicker of variantPickers) {
      if (variantPicker instanceof HTMLElement) {
        variantPicker.dataset.hideLabels = hideLabels;
      }
    }
  }

  /**
   * Injects colour swatches into the quick buy modal when enabled.
   * @param {Element} modalContent
   * @param {string} [selectedProductId]
   */
  #injectQuickBuyColourSwatches(modalContent, selectedProductId = '') {
    if (this.dataset.showColourSwatches === 'false') return;

    const swatchTemplate = this.querySelector('template[data-quick-buy-colour-swatches]');
    if (!(swatchTemplate instanceof HTMLTemplateElement)) return;

    modalContent.querySelector('.product-colour-swatches--quick-buy')?.remove();

    const swatchFragment = swatchTemplate.content.cloneNode(true);
    if (!(swatchFragment instanceof DocumentFragment)) return;

    const activeProductId = selectedProductId || this.dataset.productId || '';
    const swatchButtons = swatchFragment.querySelectorAll('button[data-swatch-product-id]');
    for (const swatchButton of swatchButtons) {
      if (!(swatchButton instanceof HTMLButtonElement)) continue;

      const isSelected = swatchButton.dataset.swatchProductId === activeProductId;
      swatchButton.classList.toggle('is-selected', isSelected);
      swatchButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    }

    const insertionTarget =
      modalContent.querySelector('variant-picker') ||
      modalContent.querySelector('product-form-component') ||
      modalContent.firstElementChild;

    if (insertionTarget?.parentNode) {
      insertionTarget.parentNode.insertBefore(swatchFragment, insertionTarget);
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.disallowBuy === 'true') return;

    if (this.dataset.hasSizeSelector === 'true') {
      return;
    }

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    this.activeQuickAdd = null;
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
